/**
 * DateTimePicker Component
 * Calendar popover with optional time input.
 * Matches Figma calendar modal: terracotta selected day, dark bg, time field.
 */

import { format } from "date-fns"
import { CalendarIcon, ChevronDown } from "lucide-react"
import { useCallback, useState } from "react"
import { Calendar } from "@/components/ui/calendar"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib"

interface DateTimePickerProps {
  label: string
  /** ISO datetime string (e.g. "2025-09-10T19:00") */
  value?: string
  onChange?: (value: string) => void
  required?: boolean
  error?: string
  onClearError?: () => void
  placeholder?: string
  showTime?: boolean
  disabled?: boolean
  disablePastDates?: boolean
  className?: string
}

const to24Hour = (h: number, p: "AM" | "PM") => {
  if (p === "AM") return h % 12
  return (h % 12) + 12
}

export function DateTimePicker({
  label,
  value,
  onChange,
  required,
  error,
  onClearError,
  placeholder = "Select date",
  showTime = true,
  disabled,
  disablePastDates,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false)

  // Parse the ISO string into a Date and time string
  const dateValue = value ? new Date(value) : undefined

  const [hour, setHour] = useState(() => {
    const h = dateValue?.getHours() ?? 19
    return String(h % 12 || 12)
  })
  const [minute, setMinute] = useState(() => {
    return String(dateValue?.getMinutes() ?? 0).padStart(2, "0")
  })
  const [period, setPeriod] = useState<"AM" | "PM">(() => {
    const h = dateValue?.getHours() ?? 19
    return h >= 12 ? "PM" : "AM"
  })

  const displayText =
    dateValue && !Number.isNaN(dateValue.getTime())
      ? showTime
        ? format(dateValue, "MMM d, yyyy 'at' h:mm a")
        : format(dateValue, "MMM d, yyyy")
      : ""

  const updateTime = (h24: number, m: number) => {
    if (!dateValue || Number.isNaN(dateValue.getTime())) return
    onClearError?.()
    const updated = new Date(dateValue)
    updated.setHours(h24, m, 0, 0)
    onChange?.(format(updated, "yyyy-MM-dd'T'HH:mm"))
  }

  const handleDateSelect = useCallback(
    (day: Date | undefined) => {
      if (!day) return
      onClearError?.()
      const result = new Date(day)
      const h24 = to24Hour(Number(hour), period)
      result.setHours(h24, Number(minute), 0, 0)
      onChange?.(format(result, "yyyy-MM-dd'T'HH:mm"))
    },
    [hour, minute, period, onChange, onClearError],
  )

  const handleHourChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHour(e.target.value)
    updateTime(to24Hour(Number(e.target.value), period), Number(minute))
  }

  const handleMinuteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMinute(e.target.value)
    updateTime(to24Hour(Number(hour), period), Number(e.target.value))
  }

  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPeriod = e.target.value as "AM" | "PM"
    setPeriod(newPeriod)
    updateTime(to24Hour(Number(hour), newPeriod), Number(minute))
  }

  return (
    <div className={cn("relative space-y-2", className)}>
      <Label
        className={cn(
          "text-xs leading-5 font-normal uppercase mb-[1px]",
          error ? "text-brand-terracotta-bright" : "text-body",
        )}
      >
        {label}
        {required && (
          <span className={error ? "text-brand-terracotta-bright" : "text-body"}>*</span>
        )}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "w-full pb-2 pt-1 ps-0 rounded-none bg-transparent text-sm font-semibold appearance-none focus:outline-none transition-all duration-200 border-b text-left flex items-center justify-between pr-2",
              error
                ? "border-brand-terracotta-bright text-brand-terracotta-bright"
                : displayText
                  ? "border-input text-[var(--color-chat-text)]"
                  : "border-input text-body",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span className="flex items-center gap-1">
              <CalendarIcon className="size-4 text-primary" />
              {displayText || placeholder}
            </span>
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={10}
          className="w-auto bg-popover border border-border rounded-[20px] p-[20px]"
        >
          <div className="flex flex-col gap-[15px]">
            <Calendar
              mode="single"
              selected={dateValue}
              onSelect={handleDateSelect}
              disabled={disablePastDates ? { before: new Date() } : undefined}
              className="p-0"
            />

            {showTime && (
              <div className="flex items-center justify-between border-t border-text-primary/20 pt-[15px]">
                <span className="text-[14px] text-text-primary">Time</span>
                <div className="flex items-center gap-1.5">
                  <select
                    value={hour}
                    onChange={handleHourChange}
                    className="bg-text-primary text-my-rituality-bg text-[14px] font-semibold rounded-[8px] px-[8px] py-[8px] focus:outline-none appearance-none cursor-pointer"
                  >
                    {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(h => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                  <span className="text-text-primary font-semibold">:</span>
                  <select
                    value={minute}
                    onChange={handleMinuteChange}
                    className="bg-text-primary text-my-rituality-bg text-[14px] font-semibold rounded-[8px] px-[8px] py-[8px] focus:outline-none appearance-none cursor-pointer"
                  >
                    {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map(
                      m => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ),
                    )}
                  </select>
                  <select
                    value={period}
                    onChange={handlePeriodChange}
                    className="bg-text-primary text-my-rituality-bg text-[14px] font-semibold rounded-[8px] px-[8px] py-[8px] focus:outline-none appearance-none cursor-pointer"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="mt-1 text-xs text-brand-terracotta-bright">{error}</p>}
    </div>
  )
}
